<template>
  <div
    v-if="hasStoredCards"
    data-cy="user-stored-credit-cards"
  >
    <slot name="stored-card-header" />
    <b-form-group>
      <template v-slot:label>
        <slot name="stored-card-message">
          Select from stored credit cards
        </slot>
      </template>
      <template v-for="(paymentMethod, idx) in userStoredCardOptions">
        <b-form-radio
          :id="paymentMethod.id"
          :key="paymentMethod.id"
          v-model="selectedCard"
          :class="`user-stored-card stored-card-no--${idx + 1}`"
          name="user-stored-card-option"
          :value="paymentMethod.id"
          :disabled="disabled"
        >
          <square-payments-storedcard-display
            :account="account"
            :card-details="paymentMethod"
          />
        </b-form-radio>
      </template>
      <b-form-radio
        v-if="allowOther"
        key="new"
        v-model="selectedCard"
        data-cy="stored-card-select-new-card"
        class="user-stored-card stored-card-no--new"
        name="user-stored-card-option"
        value="new"
        :disabled="disabled"
      >
        <div class="stored-credit-card-details">
          <slot name="enter-new-card-label">
            Enter new card
          </slot>
        </div>
      </b-form-radio>
    </b-form-group>
  </div>
</template>

<script>

import SquarePaymentsMixin from '../mixins/square-payments'

export default {
  name: 'square-payments-stored-card-select',
  mixins: [
    SquarePaymentsMixin
  ],
  props: {
    value: {
      type: String,
      required: true
    },
    account: {
      type: Object,
      required: true
    },
    availableCards: {
      type: Array,
      required: false,
      default: null
    },
    allowOther: {
      type: Boolean,
      required: false,
      default: true
    },
    noExpiryDate: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      loading: false,
      userStoredCards: []
    }
  },
  computed: {
    userid: function() {
      return this.account.userid
    },
    paymentMethods: function() {
      return this.userStoredCardOptions.map(opt => opt.id)
    },
    hasStoredCards: function() {
      return !this.isAnonymous && Object.keys(this.userStoredCardOptions).length > 0
    },
    userStoredCardOptions: function() {
      if (Array.isArray(this.availableCards) && this.availableCards.length) {
        return this.availableCards
      }
      else if (Array.isArray(this.userStoredCards) && this.userStoredCards.length) {
        return this.userStoredCards
      }
      else {
        return []
      }
    },
    selectedCard: {
      get: function() {
        if (this.userStoredCardOptions.length) {
          return this.value
        }
        return ''
      },
      set: function(val) {
        // console.log(`Emit selected card: ${val}`)
        this.$emit('input', val)
      }
    }
  },
  watch: {
    loading: function(val) {
      this.$emit('loading', val)
    },
    selectedCard: function(val) {
      this.$emit('selected-card', val)
    },
    userStoredCardOptions: function(newCards, oldCards) {
      // When stored cards are loaded, ensure proper selection
      if (newCards.length > 0 && oldCards.length === 0) {
        this.ensureValidSelection()
      }
    }
  },
  mounted: function() {
    let p = Promise.resolve()
    if (!this.availableCards && !this.isAnonymous) {
      this.loading = true
      p = this.$store.dispatch('square/loadCustomerCards', { userid: this.userid })
        .then(storedCards => {
          this.loading = false
          this.userStoredCards = storedCards
        })
        .catch(err => {
          this.loading = false
          const error = new Error(`Error loading customer stored payment methods: ${err.message}`)
          this.$emit('error', error)
        })
    }

    return p.then(_ => {
      this.ensureValidSelection()

      // Emit ready event after all initialization is complete
      this.$nextTick(() => {
        this.$emit('ready', {
          hasStoredCards: this.hasStoredCards,
          storedCardsCount: this.userStoredCardOptions.length,
          selectedCard: this.selectedCard
        })
      })
    })
  },
  methods: {
    ensureValidSelection: function() {
      // Always select first stored card if no valid selection exists
      if (this.userStoredCardOptions.length > 0) {
        if (!this.value || this.value.trim() === '' || this.value.trim() === 'new' || !this.paymentMethods.includes(this.value)) {
          this.$emit('input', this.userStoredCardOptions[0].id)
          return true
        }
      }
      else {
        // No stored cards available, default to 'new'
        if (!this.value || this.value.trim() === '' || !this.paymentMethods.includes(this.value)) {
          this.$emit('input', 'new')
          return true
        }
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>

  .user-stored-card {
    display: flex;
    align-items: center;

    input {
      margin-right: 0.75rem;
      flex-shrink: 0;
    }

    // Prevent flex styling from affecting Square iframe elements
    ::v-deep iframe,
    ::v-deep .sq-card-component,
    ::v-deep [class*="sq-"] {
      display: block !important;
      align-items: unset !important;
      height: auto !important;
    }
  }

  .stored-card-no--new {
    display: flex;
    align-items: center;

    input {
      margin-right: 0.75rem;
      flex-shrink: 0;
    }

    // Target only the direct child to avoid affecting Square iframe, but maintain padding
    .stored-credit-card-details {
      margin-left: 0;
      padding: 0.5rem 0.75rem;
      display: flex;
      align-items: center;
      // Remove min-height to prevent affecting Square iframe input fields
      // min-height: 2.5rem;
    }

    // Prevent flex styling from affecting Square iframe elements
    ::v-deep iframe,
    ::v-deep .sq-card-component,
    ::v-deep [class*="sq-"] {
      display: block !important;
      align-items: unset !important;
      height: auto !important;
    }
  }

  // Additional isolation for Square form elements to prevent CSS inheritance
  ::v-deep {
    // Target Square card container specifically
    #square-card-container {
      iframe,
      .sq-card-component,
      [class*="sq-"] {
        display: block !important;
        align-items: unset !important;
        height: auto !important;
        min-height: unset !important;
        max-height: unset !important;
        flex: none !important;
      }

      // Reset any Bootstrap or component styles that might interfere
      input,
      div,
      span {
        display: block !important;
        align-items: unset !important;
        height: auto !important;
        line-height: normal !important;
      }
    }
  }
</style>
